import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonGroup, Form, OverlayTrigger, ToggleButton, Tooltip } from 'react-bootstrap';
import VocabularyThumbnail from './vocabularyThumbnail';
import { faBars, faCardsBlank, faGrid2, faGrid4, faList, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import CustomToast from '../../../../components/Toast';
import { faClipboardList } from '@fortawesome/pro-light-svg-icons';
import Model from '../../../../components/Modal/index';
import SkeletonGrid from '../../../../components/SkeletonLoader/SkeletonGrid';
import { useGetVocabularyViewModeMutation, useUpdateVocabularyViewModeMutation } from '../../../../features/authentication/persistApiSlice';
import { useActivitySearchContext } from '../../context';

const VocabularyList = ({
  count,
  vocabularies,
  setVocabulariesList,
  selectedVocabularies,
  setSelectedVocabularies,
  isError,
  handleRetrySearch,
  isGettingRandomVocabularies,
  isSearching,
  setDateBasedKey,
  goToVocabularyIdSearchCall,
  mapSearchResults,
  vocabulariesPerPage  ,
  selectRandomVocabulariesCall,
  callByApiIdentifier,
  selectedPage,
  setSelectedPage,
  onRemove
}) => {
  const goToVocabularyIdRef = useRef();
  const randomVocabularyRef = useRef();
  const [goToVocabularyIdErrorMessage, setGoToVocabularyIdErrorMessage] = useState('');
  const [openVocabularyClipBoardModal, setOpenVocabularyClipBoardModal] = useState(false);
  const [viewMode , setViewMode ] = useState(0);
  const [getVocabularyViewMode] = useGetVocabularyViewModeMutation();
  const [updateVocabularyViewMode] = useUpdateVocabularyViewModeMutation();
  const context = useActivitySearchContext();

  useEffect(() => {
    setGoToVocabularyIdErrorMessage('');
  }, [vocabularies]);

  useEffect(() => {
    getPersistViewModeAsync();
  }, []); 

  const getPersistViewModeAsync = async () =>{
    var vocabularyView = await getVocabularyViewMode().unwrap();
    setViewMode(vocabularyView);
  };

  const updateVocabularyViewModeAsync = async (vocabularyViewMode)=>{
    setViewMode(vocabularyViewMode);
    const options = {
      vocabularyViewMode : vocabularyViewMode
    };
    await updateVocabularyViewMode(options).unwrap();
  };

  const handleSelectAllQuestions = (selectAll) => {

    if (selectAll) {
      if (selectedVocabularies.length >= 100) {
        CustomToast('You have already reached the maximum number of vocabularies', 'error');
        return;
      }

      var numberOfVocabulariesToSelect = vocabularies.filter(q => !q.isSelected && q.blockedText === null).length + selectedVocabularies.length <= 100 ? vocabularies.filter(q => !q.isSelected && q.blockedText === null).length : 100 - selectedVocabularies.length;

      if (numberOfVocabulariesToSelect < vocabularies.filter(q => !q.isSelected && q.blockedText === null).length) CustomToast(`Maximum number of vocabularies reached.  ${numberOfVocabulariesToSelect} vocabulary${numberOfVocabulariesToSelect == 1 ? ' has' : 's have'} been selected.`, 'warning');
      var tempVocabularyList = [...vocabularies];
      var tempSelectedVocabularies = [...selectedVocabularies];

      for (var i = 0; i < tempVocabularyList.length; i++) {
        if (numberOfVocabulariesToSelect == 0) break;

        if(tempVocabularyList[i].blockedText === null && tempSelectedVocabularies.find(q => q.questionId === tempVocabularyList[i].questionId) === undefined)
        {
          tempVocabularyList[i].isSelected = true;
          tempSelectedVocabularies.push(tempVocabularyList[i]);
          numberOfVocabulariesToSelect--;
        }
      }

      setSelectedVocabularies(tempSelectedVocabularies);
      setVocabulariesList(tempVocabularyList);
    } else {
      setSelectedVocabularies(selectedVocabularies.filter(q => !vocabularies.some(ql => `${q.questionId}` == `${ql.questionId}`)));
      setVocabulariesList(vocabularies.map((vocabulary) => ({ ...vocabulary, isSelected: false })));
    }
  };

  const handleGoToVocabularyIdChange = (e) => {
    if (e.target.value.length > 0) {
      e.target.value = e.target.value.replace(/\D/g, '');
    } else{
      setGoToVocabularyIdErrorMessage('');
    }
  };

  const handleGoToVocabularyId = () => {
    const inputValue = goToVocabularyIdRef.current.value;
    const MAX_VOCABULARY_ID = 2147483647;

    if (vocabularies.length === 1 && inputValue === String(vocabularies[0].questionId)) return;
    if (vocabularies.length === 1 && count === 1) return;
    if (!inputValue && vocabularies.length > 1) return;

    if (goToVocabularyIdRef.current.value.length <= 0 && vocabularies.length == 1) {
      callByApiIdentifier();
      setSelectedPage(selectedPage);
      return;
    }

    if (!/^\d+$/.test(inputValue) || Number(inputValue) > MAX_VOCABULARY_ID) {
      setGoToVocabularyIdErrorMessage('Invalid Vocabulary ID');
      return;
    }

    const existingVocabulary = vocabularies.find(q => String(q.questionId) === inputValue);
    if (existingVocabulary) {
      setVocabulariesList(vocabularies.filter(q => String(q.questionId) === inputValue));
      return;
    }

    goToVocabularyIdSearchCall({ vocabularyId: inputValue, pageSize: vocabulariesPerPage }).then((results) => {
      if (results?.error?.data?.errorMessage) {
        setGoToVocabularyIdErrorMessage(results.error.data.errorMessage);
        return;
      }
      setGoToVocabularyIdErrorMessage('');
      mapSearchResults(results);
    });
  };

  const handleRandomlySelectVocabularies = async () => {
    if (!randomVocabularyRef.current?.value) return;
    
    if (selectedVocabularies.length == count) {
      CustomToast('All vocabularies have already been selected', 'warning');
      return;
    }
    var response = await selectRandomVocabulariesCall({ alreadySelectedQuestions: selectedVocabularies.map(q => q.questionId), questionCount: randomVocabularyRef.current.value });
    
    if (response.error) {
      CustomToast('Could not get vocabularies', 'error');
      return;
    }
    
    if (Number(randomVocabularyRef.current.value) + selectedVocabularies.length > 100) {
      CustomToast(`Maximum number of vocabularies reached.  ${response.data.length} vocabulary${response.data.length == 1 ? ' has' : 's have'} been selected.`, 'warning');
    } else if (response.data.length < randomVocabularyRef.current.value) {
      CustomToast(`All remaining vocabularies have been selected. ${response.data.length} vocabulary${response.data.length == 1 ? ' has' : 's have'} been selected.`, 'warning');
    }
    
    setSelectedVocabularies(selectedVocabularies.concat(response.data.map((vocabulary) => ({ ...vocabulary, isSelected: true }))));
    setVocabulariesList(vocabularies.map((vocabulary) => ({ ...vocabulary, isSelected: vocabulary.isSelected || response.data.some(q => `${q.questionId}` == `${vocabulary.questionId}`) })));
    
    setOpenVocabularyClipBoardModal(true);
  };

  const handleSelectRandomVocabularyChange = (e) => {
    if (e.target.value.length > 0) {
      e.target.value = e.target.value.replace(/\D/g, '');
    }

    if (e.target.value > 100) {
      e.target.value = 100;
    }
  };

  const questionViewModeOptions = [
    { tooltip: 'List', value: 0, icon:<FontAwesomeIcon icon={faBars} className='fa-lg'/> },
    { tooltip: 'Tile Large', value: 1, icon:<FontAwesomeIcon icon={faGrid2} className='fa-lg'/> },
    { tooltip: 'Tile Small', value: 2, icon:<FontAwesomeIcon  icon={faGrid4} className='fa-lg'/> },
  ];

  return (
    <>
      <div className='d-flex flex-column whiteCard w-100 search-results-container'>
        <div className='d-flex flex-row justify-content-between flex-wrap gap-2'>
          <div className='d-flex flex-row gap-2 align-items-center flex-wrap'>
            <h1>Vocabularies that match your search ({count})</h1>
          </div>
          <div className={`d-flex flex-row align-items-center gap-3 mb-2 flex-wrap ${vocabularies.every(q => q.blockedText) && 'd-none'}`} style={{ color: (count > 0 ? '#F7931E' : '#F7BF7E'), fontSize: '14px', cursor: (count > 0 ? 'pointer' : 'default') }}>
            <div  onClick={() => handleSelectAllQuestions(!(vocabularies.filter(q=>q.isSelected || q.blockedText!=null).length == vocabularies.length))}>
              <FontAwesomeIcon color={(count > 0 ? '#F7931E' : '#F7BF7E')} className='fs-15 mr-2' icon={faList} />
              <span>{`${(vocabularies.every(q => q.isSelected) || (vocabularies.filter(q => q.isSelected || q.blockedText).length === 
                vocabularies.length)) && count > 0 ? 'Unselect' : 'Select'} all vocabularies on page`}
              </span>
            </div>
          </div>
        </div>
        {!isError && context.activityName &&
        <div className='mt-3 mb-3 assignmentBadge'>
          <FontAwesomeIcon icon={faCardsBlank} />
          Added from: <strong>{context.activityName}</strong>
        </div>
        }

        <Model
          isOpen={openVocabularyClipBoardModal}
          onClose={() => setOpenVocabularyClipBoardModal(false)}
          contentWidth="700px"
          titleName='Randomly selected vocabularies have been added'>
          <div className='text-center'>
          Vocabularies have been added to your clipboard to review.You can view <br/>your clipboard in the toolbar at the bottom of this screen
          </div>
          <div className='w-100 d-flex justify-content-center mt-3'>
            <div className='selectedStudentsClipboard'><FontAwesomeIcon icon={faClipboardList} /></div>
          </div>
        </Model>
        {isError && <button className='dataFetchError' onClick={() => handleRetrySearch()}>There was an error getting the data. Click to try again</button>}

        {!isError && <>
          <div className='goToQuestionRow'>
            <div className='d-flex flex-row align-items-center gap-2 flex-wrap'>
              <div className='fw-semibold'>Randomly select vocabularies (Max: 100)</div>
              <Form.Control type='text' className='mx-2 w-15'  style={{ minWidth: '96px', maxWidth: '100%' }} disabled={isGettingRandomVocabularies || count == 0} ref={randomVocabularyRef} defaultValue='10' onChange={(e) => handleSelectRandomVocabularyChange(e)} maxLength={3} />

              <div>
                <Button variant='castleWhite' className='btn-width' onClick={() => handleRandomlySelectVocabularies()}>Select</Button>
              </div>
            </div>
         
            <div className='d-flex align-items-center flex-wrap justify-content-start justify-content-lg-end gap-2 w-auto'>
              <div className='d-flex gap-3 justify-content-center p-3 rounded-3'>
                <ButtonGroup>
                  {questionViewModeOptions.map((questionViewMode, idx) => (
                    <>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip className="custom-tooltip">{questionViewMode.tooltip}</Tooltip>
                        }>
                        <ToggleButton
                          className='btn-outline-castleRed align-content-center'
                          key={idx}
                          id={`radio-${idx}`}
                          type="radio"
                          variant=''
                          name="radio"
                          value={questionViewMode.value}
                          checked={viewMode === questionViewMode.value}
                          onChange={(e) => updateVocabularyViewModeAsync(parseInt(e.currentTarget.value))}
                        >
                          {questionViewMode.icon}
                        </ToggleButton>
                      </OverlayTrigger>
                    </>

                  ))}
                </ButtonGroup>
              </div>
              <div className='d-flex flex-column' style={{ width: '155px', maxWidth: '155px' }}>
                <Form.Control type='text' placeholder='Go to Vocabulary ID' style={{ width: '155px', maxWidth: '155px' }} ref={goToVocabularyIdRef}
                  onChange={(e) => handleGoToVocabularyIdChange(e)}
                  onKeyUp={(e) => { if (e.key == 'Enter') handleGoToVocabularyId(); }}
                  onBlur={() => handleGoToVocabularyId()}
                  disabled={count == 0} />
                <span style={{ color: '#F84646' }}>{goToVocabularyIdErrorMessage}</span>
              </div>
            </div>
          </div>
          {count > 0 && !isSearching ?
            <div className={`questionsGrid ${viewMode == 0 ? 'tileList' : viewMode == 1 ? 'tileLarge': 'tileSmall'}`}>
              {vocabularies.map((vocabulary) => {
                return (
                  <VocabularyThumbnail
                    key={`${vocabulary.id}`}
                    vocabulary={vocabulary}
                    defaultChecked={selectedVocabularies.includes(vocabulary)}
                    selectedVocabularies={selectedVocabularies}
                    setSelectedVocabularies={setSelectedVocabularies}
                    setDateBasedKey={setDateBasedKey}
                    viewMode={viewMode}
                    onRemove={onRemove}
                  />
                );
              })}
            </div>
            :
            count == 0 && !isSearching ?
              <div className='noResultsContainer'>
                <div className='noResultsBox'>
                  <div className='noResultsIconBox mb-4'>
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  </div>
                  <div className='fw-500' style={{ color: '#A4A4A4' }}>No vocabularies were found matching your filter(s).  Please try another search.</div>
                </div>
              </div>
              :
              <>
                <SkeletonGrid
                  rowCount={3}
                  colCount={viewMode == 0 ? 1 : viewMode == 1 ? 2 : 4}
                  className='dummy-thumbnail rounded'/>
              </>
          }
        </>
        }
      </div>
    </>
  );
};

VocabularyList.propTypes = {
  count: PropTypes.number,
  vocabularies: PropTypes.array,
  setVocabulariesList: PropTypes.func,
  selectedVocabularies: PropTypes.array,
  setSelectedVocabularies: PropTypes.func,
  isError: PropTypes.bool,
  handleRetrySearch: PropTypes.func,
  isGettingRandomVocabularies: PropTypes.bool,
  isSearching: PropTypes.bool,
  setDateBasedKey: PropTypes.func,
  goToVocabularyIdSearchCall: PropTypes.func,
  mapSearchResults: PropTypes.func,
  vocabulariesPerPage: PropTypes.number,
  selectRandomVocabulariesCall: PropTypes.func,
  callByApiIdentifier: PropTypes.func,
  selectedPage: PropTypes.number,
  setSelectedPage: PropTypes.func,
  onRemove: PropTypes.func
};

export default VocabularyList;
